import { useRouter } from 'next/router';
import { FC, useCallback, useEffect, useMemo } from 'react';

import { Optional } from '@common/type/CommonTypes';
import { Box } from '@mui/material';
import { useInView } from 'react-intersection-observer';
import { Element } from 'react-scroll';

import CourseReviewContent from './courseReviewContent';

import Container from '@components/store/container';
import { useCourseReview } from '@hooks/course/courseReview/useCourseReview';
import { COURSE_REVIEW_DEFAULT_PAGE } from '@hooks/course/courseReview/useCourseReviewListing';
import useCourseDetail from '@hooks/course/useCourseDetail';
import useCourseEnrollment from '@hooks/course/useCourseEnrollment';

type CourseReviewProps = {
  scrollElementName: string;
  isDisplayCourseReview: boolean;
};

const CourseReview: FC<CourseReviewProps> = ({ scrollElementName, isDisplayCourseReview }) => {
  const router = useRouter();
  const idName = router.query.idName as Optional<string>;

  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0 });

  const {
    handleFetchCourseReviewSummary,
    handleFetchCourseReviewList,
    handleFetchCourseReviewMe,
    isCourseReviewListDataLoading,
    isCourseReviewSummaryDataLoading,
    courseReviewListData,
  } = useCourseReview({
    idName,
  });

  const { hasEnrolled } = useCourseEnrollment();

  const { isCourseDetailLoading } = useCourseDetail();

  const handleFetchCourseReview = useCallback(async () => {
    handleFetchCourseReviewList({ page: COURSE_REVIEW_DEFAULT_PAGE });
    handleFetchCourseReviewSummary();
    if (hasEnrolled) {
      handleFetchCourseReviewMe();
    }
  }, [handleFetchCourseReviewList, handleFetchCourseReviewMe, handleFetchCourseReviewSummary, hasEnrolled]);

  useEffect(() => {
    if (isDisplayCourseReview && inView) {
      handleFetchCourseReview();
    }
  }, [handleFetchCourseReview, inView, isDisplayCourseReview]);

  const isLoadingCourseReviewContent = useMemo(() => {
    if (
      !courseReviewListData &&
      (isCourseDetailLoading || isCourseReviewListDataLoading || isCourseReviewSummaryDataLoading)
    ) {
      return true;
    }
    return false;
  }, [courseReviewListData, isCourseDetailLoading, isCourseReviewListDataLoading, isCourseReviewSummaryDataLoading]);

  if (!isDisplayCourseReview && !isCourseDetailLoading) {
    return null;
  }

  return (
    <Element name={scrollElementName}>
      <Box ref={ref} minHeight={368}>
        {inView ? (
          <Container containerSx={{ px: { xs: 3, lg: 0 }, py: 6 }}>
            <CourseReviewContent
              isLoading={isLoadingCourseReviewContent}
              handleFetchCourseReview={handleFetchCourseReview}
            />
          </Container>
        ) : null}
      </Box>
    </Element>
  );
};

export default CourseReview;
